// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/mission-execution/tabs/TaskOverviewTab.jsx

import React from "react";
import ExecutionSection from "../shared/ExecutionSection";
import ExecutionMetricTile from "../shared/ExecutionMetricTile";
import ExecutionContextCard from "../shared/ExecutionContextCard";
import ExecutionActionRow from "../shared/ExecutionActionRow";
import TaskIntelligencePanel from "../TaskIntelligencePanel";

function displayValue(value, fallback = "—") {
  if (value === null || value === undefined || value === "") return fallback;
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  if (Array.isArray(value)) {
    const text = value
      .map((item) => displayValue(item, ""))
      .filter(Boolean)
      .join(", ");
    return text || fallback;
  }
  if (typeof value === "object") {
    const candidate =
      value.label ||
      value.title ||
      value.name ||
      value.value ||
      value.status ||
      value.state ||
      value.id;
    if (
      candidate !== undefined &&
      candidate !== null &&
      candidate !== value
    ) {
      return displayValue(candidate, fallback);
    }
    try {
      return JSON.stringify(value);
    } catch (error) {
      return fallback;
    }
  }
  return String(value);
}

function humanizeDisplayValue(value, fallback = "—") {
  const raw = displayValue(value, "").trim();
  if (!raw) return fallback;

  const normalized = raw.replace(/_/g, " ").replace(/\s+/g, " ").trim();
  const lower = normalized.toLowerCase();
  const exactLabels = {
    soc2: "SOC2",
    "soc 2": "SOC2",
    nist: "NIST",
    iso: "ISO",
    hipaa: "HIPAA",
    ccpa: "CCPA",
    aml: "AML",
    kyc: "KYC",
    fda: "FDA",
    osha: "OSHA",
    sop: "SOP",
    aws: "AWS",
    s3: "S3",
    iam: "IAM",
    waf: "WAF",
    api: "API",
    "risk queue": "Risk Queue",
    "in progress": "In Progress",
    "not linked": "Not Linked",
    "not loaded": "Not Loaded",
    "ready for validation": "Ready for Validation",
    "ready to work": "Ready to Work",
    "awaiting validation": "Awaiting Validation",
    "awaiting approval": "Awaiting Approval",
  };

  if (exactLabels[lower]) return exactLabels[lower];

  return normalized
    .split(" ")
    .map((word) => {
      const key = word.toLowerCase();
      if (exactLabels[key]) return exactLabels[key];
      if (/^cc\d+(?:\.\d+)?$/i.test(word)) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

export default function TaskOverviewTab({
  task = {},
  onOpenTab,
  intelligence = null,
  intelligenceLoading = false,
  intelligenceError = "",
  intelligenceQuestion = "",
  onRequestIntelligence,
}) {
  const taskStatus = humanizeDisplayValue(
    task.status || task.task_status,
    "Status Unknown"
  );
  const priority = humanizeDisplayValue(
    task.priority_label || task.priority,
    "Priority Unknown"
  );
  const framework = humanizeDisplayValue(
    task.framework || task.affected_framework,
    "Framework Unknown"
  );
  const connector = humanizeDisplayValue(
    task.connector || task.affected_connector || task.provider,
    "Connector Unknown"
  );
  const validation = humanizeDisplayValue(
    task.validation_status,
    "Validation Unknown"
  );
  const workflow = humanizeDisplayValue(task.workflow_status, "Not Linked");

  return (
    <>
      <ExecutionSection
        title="Task Overview"
        description="Review the selected task, its current state, and the next safe operator actions."
        badge={taskStatus}
        badgeTone="default"
      >
        <ExecutionContextCard
          title={displayValue(
            task.title || task.summary || task.name || "Selected task"
          )}
          chips={[
            { label: priority, tone: "warn" },
            { label: framework, tone: "default" },
            { label: connector, tone: "neutral" },
          ]}
          dueLabel={`Due: ${displayValue(
            task.due_date || task.sla_due_at || task.dueLabel
          )}`}
          meta={[
            {
              label: "Task ID",
              value: task.task_id || task.work_item_id || task.id,
              wrap: true,
            },
            {
              label: "Control",
              value: task.control_id || task.control,
              wrap: true,
            },
            {
              label: "Resource",
              value: task.resource_id || task.resource,
              wrap: true,
            },
            { label: "Validation", value: validation, wrap: true },
            { label: "Workflow", value: workflow, wrap: true },
            {
              label: "Owner",
              value:
                task.owner ||
                task.assignee ||
                task.assigned_to ||
                task.owner_name,
              wrap: true,
            },
          ]}
          context={displayValue(
            task.description || task.reason || task.context,
            "No task context was provided."
          )}
          actions={[
            {
              key: "guidance",
              label: "Open Guidance",
              onClick: () => onOpenTab?.("guidance"),
            },
            {
              key: "validation",
              label: "Run Validation",
              onClick: () => onOpenTab?.("validation"),
            },
            {
              key: "evidence",
              label: "View Evidence",
              onClick: () => onOpenTab?.("evidence"),
            },
          ]}
        />
      </ExecutionSection>

      <TaskIntelligencePanel
        intelligence={intelligence}
        loading={intelligenceLoading}
        error={intelligenceError}
        activeQuestion={intelligenceQuestion}
        onRequest={onRequestIntelligence}
      />

      <ExecutionSection
        title="Operating Snapshot"
        description="Current task status and readiness at a glance."
      >
        <div className="task-overview-metric-grid">
          <ExecutionMetricTile
            label="Priority"
            value={priority}
            color="#facc15"
          />
          <ExecutionMetricTile label="Task Status" value={taskStatus} />
          <ExecutionMetricTile
            label="Validation"
            value={validation}
            color="#60a5fa"
          />
          <ExecutionMetricTile
            label="Workflow"
            value={workflow}
            color="#34d399"
          />
        </div>

        <div className="task-overview-snapshot-actions">
          <ExecutionActionRow
            actions={[
              { key: "start", label: "Start Work" },
              {
                key: "ready-validation",
                label: "Mark Ready for Validation",
              },
              { key: "closure", label: "View Closure Guard" },
            ]}
          />
        </div>
      </ExecutionSection>
    </>
  );
}
